import { React, useState, useEffect, useRef, useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import axios, { AxiosError } from 'axios';
import PurchasingPowerParity from './PurchasingPowerParity.tsx';
// import BuyingPowerIntroduction from './BuyingPowerIntroduction.tsx';
import Footer from './Footer.tsx';

type Country = {
  code: string;
  name: string;
  currency?: string;
  symbol?: string;
  flag: string;
  ppp?: number;
};

const Calculator = () => {
  const [calculatorType, setCalculatorType] = useState<'ppp' | 'buyingPower'>('ppp');
  const [countries, setCountries] = useState<Country[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [country1, setCountry1] = useState<Country | null>({
    code: 'USA',
    name: 'United States',
    currency: 'USD',
    symbol: '$',
    flag: 'https://flagcdn.com/w320/us.png'
  });
  const [country2, setCountry2] = useState<Country | null>({
    code: 'IND',
    name: 'India',
    currency: 'INR',
    symbol: '₹',
    flag: 'https://flagcdn.com/w320/in.png'
  });
  const [amount, setAmount] = useState(localStorage.getItem('amount') || '');
  const [result, setResult] = useState(() => {
    const storedResult = localStorage.getItem('result');
    return storedResult ? JSON.parse(storedResult) : { amount: 0, fromAmount: 0 };
  });
  const [error, setError] = useState('');
  const [hasTriedLoading, setHasTriedLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDropdown1, setShowDropdown1] = useState(false);
  const [showDropdown2, setShowDropdown2] = useState(false);
  const [searchQuery1, setSearchQuery1] = useState('');
  const [searchQuery2, setSearchQuery2] = useState('');
  const [inflationDataLoading, setInflationDataLoading] = useState(false);
  const [amountError, setAmountError] = useState('');

  const dropdown1Ref = useRef<HTMLDivElement>(null);
  const dropdown2Ref = useRef<HTMLDivElement>(null);
  const [referenceYear, setReferenceYear] = useState(localStorage.getItem('referenceYear') || '2020');
  const [targetYear, setTargetYear] = useState(localStorage.getItem('targetYear') || '2023');
  const [showReferenceYearDropdown, setShowReferenceYearDropdown] = useState(false);
  const [showTargetYearDropdown, setShowTargetYearDropdown] = useState(false);
  const referenceYearDropdownRef = useRef<HTMLDivElement>(null);
  const targetYearDropdownRef = useRef<HTMLDivElement>(null);
  const [inflationData, setInflationData] = useState<Record<string, number>>({});
  const [usingDefaultInflationData, setUsingDefaultInflationData] = useState(false);

  // Add state to track final selected countries
  const [finalCountry1, setFinalCountry1] = useState<Country | null>(null);
  const [finalCountry2, setFinalCountry2] = useState<Country | null>(null);
  const [finalAmount, setFinalAmount] = useState<string>('');

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setAmount(value);
    setAmountError('');
  };

  const swapCountries = () => {
    setCountry1(country2);
    setCountry2(country1);
  };

  useEffect(() => {
    // Only trigger calculation if we have both countries and an amount
    // Removed automatic calculation
  }, [country1, country2, amount]);

  const COUNTRIES_LIST: Country[] = [
    
      { "code": "AFG", "name": "Afghanistan", "currency": "AFN", "symbol": "؋", "flag": "https://flagcdn.com/w320/af.png" },
      { "code": "ALB", "name": "Albania", "currency": "ALL", "symbol": "L", "flag": "https://flagcdn.com/w320/al.png" },
      { "code": "DZA", "name": "Algeria", "currency": "DZD", "symbol": "دج", "flag": "https://flagcdn.com/w320/dz.png" },
      { "code": "ASM", "name": "American Samoa", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/as.png" },
      { "code": "AND", "name": "Andorra", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/ad.png" },
      { "code": "AGO", "name": "Angola", "currency": "AOA", "symbol": "Kz", "flag": "https://flagcdn.com/w320/ao.png" },
      { "code": "AIA", "name": "Anguilla", "currency": "XCD", "symbol": "$", "flag": "https://flagcdn.com/w320/ai.png" },
      { "code": "ATA", "name": "Antarctica", "currency": "null", "symbol": "null", "flag": "https://flagcdn.com/w320/aq.png" },
      { "code": "ATG", "name": "Antigua and Barbuda", "currency": "XCD", "symbol": "$", "flag": "https://flagcdn.com/w320/ag.png" },
      { "code": "ARM", "name": "Armenia", "currency": "AMD", "symbol": "֏", "flag": "https://flagcdn.com/w320/am.png" },
      { "code": "ABW", "name": "Aruba", "currency": "AWG", "symbol": "ƒ", "flag": "https://flagcdn.com/w320/aw.png" },
      { "code": "AUS", "name": "Australia", "currency": "AUD", "symbol": "$", "flag": "https://flagcdn.com/w320/au.png" },
      { "code": "AUT", "name": "Austria", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/at.png" },
      { "code": "AZE", "name": "Azerbaijan", "currency": "AZN", "symbol": "₼", "flag": "https://flagcdn.com/w320/az.png" },
      { "code": "BHS", "name": "Bahamas", "currency": "BSD", "symbol": "$", "flag": "https://flagcdn.com/w320/bs.png" },
      { "code": "BHR", "name": "Bahrain", "currency": "BHD", "symbol": ".د.ب", "flag": "https://flagcdn.com/w320/bh.png" },
      { "code": "BGD", "name": "Bangladesh", "currency": "BDT", "symbol": "৳", "flag": "https://flagcdn.com/w320/bd.png" },
      { "code": "BRB", "name": "Barbados", "currency": "BBD", "symbol": "$", "flag": "https://flagcdn.com/w320/bb.png" },
      { "code": "BLR", "name": "Belarus", "currency": "BYN", "symbol": "Br", "flag": "https://flagcdn.com/w320/by.png" },
      { "code": "BEL", "name": "Belgium", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/be.png" },
      { "code": "BLZ", "name": "Belize", "currency": "BZD", "symbol": "BZ$", "flag": "https://flagcdn.com/w320/bz.png" },
      { "code": "BEN", "name": "Benin", "currency": "XOF", "symbol": "CFA", "flag": "https://flagcdn.com/w320/bj.png" },
      { "code": "BMU", "name": "Bermuda", "currency": "BMD", "symbol": "$", "flag": "https://flagcdn.com/w320/bm.png" },
      { "code": "BTN", "name": "Bhutan", "currency": "BTN", "symbol": "Nu.", "flag": "https://flagcdn.com/w320/bt.png" },
      { "code": "BOL", "name": "Bolivia ", "currency": "BOB", "symbol": "Bs.", "flag": "https://flagcdn.com/w320/bo.png" },
      { "code": "BES", "name": "Bonaire, Sint Eustatius and Saba", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/bq.png" },
      { "code": "BIH", "name": "Bosnia and Herzegovina", "currency": "BAM", "symbol": "KM", "flag": "https://flagcdn.com/w320/ba.png" },
      { "code": "BWA", "name": "Botswana", "currency": "BWP", "symbol": "P", "flag": "https://flagcdn.com/w320/bw.png" },
      { "code": "BVT", "name": "Bouvet Island", "currency": "NOK", "symbol": "kr", "flag": "https://flagcdn.com/w320/bv.png" },
      { "code": "BRA", "name": "Brazil", "currency": "BRL", "symbol": "R$", "flag": "https://flagcdn.com/w320/br.png" },
      { "code": "IOT", "name": "British Indian Ocean Territory", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/io.png" },
      { "code": "BRN", "name": "Brunei Darussalam", "currency": "BND", "symbol": "$", "flag": "https://flagcdn.com/w320/bn.png" },
      { "code": "BGR", "name": "Bulgaria", "currency": "BGN", "symbol": "лв", "flag": "https://flagcdn.com/w320/bg.png" },
      { "code": "BFA", "name": "Burkina Faso", "currency": "XOF", "symbol": "CFA", "flag": "https://flagcdn.com/w320/bf.png" },
      { "code": "BDI", "name": "Burundi", "currency": "BIF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/bi.png" },
      { "code": "CPV", "name": "Cabo Verde", "currency": "CVE", "symbol": "Esc", "flag": "https://flagcdn.com/w320/cv.png" },
      { "code": "KHM", "name": "Cambodia", "currency": "KHR", "symbol": "៛", "flag": "https://flagcdn.com/w320/kh.png" },
      { "code": "CMR", "name": "Cameroon", "currency": "XAF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/cm.png" },
      { "code": "CAN", "name": "Canada", "currency": "CAD", "symbol": "$", "flag": "https://flagcdn.com/w320/ca.png" },
      { "code": "CYM", "name": "Cayman Islands", "currency": "KYD", "symbol": "$", "flag": "https://flagcdn.com/w320/ky.png" },
      { "code": "CAF", "name": "Central African Republic", "currency": "XAF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/cf.png" },
      { "code": "TCD", "name": "Chad", "currency": "XAF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/td.png" },
      { "code": "CHL", "name": "Chile", "currency": "CLP", "symbol": "$", "flag": "https://flagcdn.com/w320/cl.png" },
      { "code": "CHN", "name": "China", "currency": "CNY", "symbol": "¥", "flag": "https://flagcdn.com/w320/cn.png" },
      { "code": "CXR", "name": "Christmas Island", "currency": "AUD", "symbol": "$", "flag": "https://flagcdn.com/w320/cx.png" },
      { "code": "CCK", "name": "Cocos (Keeling) Islands", "currency": "AUD", "symbol": "$", "flag": "https://flagcdn.com/w320/cc.png" },
      { "code": "COL", "name": "Colombia", "currency": "COP", "symbol": "$", "flag": "https://flagcdn.com/w320/co.png" },
      { "code": "COM", "name": "Comoros", "currency": "KMF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/km.png" },
      { "code": "COG", "name": "Congo", "currency": "XAF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/cg.png" },
      { "code": "COD", "name": "Congo ", "currency": "CDF", "symbol": "FC", "flag": "https://flagcdn.com/w320/cd.png" },
      { "code": "COK", "name": "Cook Islands", "currency": "NZD", "symbol": "$", "flag": "https://flagcdn.com/w320/ck.png" },
      { "code": "CRI", "name": "Costa Rica", "currency": "CRC", "symbol": "₡", "flag": "https://flagcdn.com/w320/cr.png" },
      { "code": "HRV", "name": "Croatia", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/hr.png" },
      { "code": "CUB", "name": "Cuba", "currency": "CUC", "symbol": "$", "flag": "https://flagcdn.com/w320/cu.png" },
      { "code": "CUW", "name": "Curaçao", "currency": "ANG", "symbol": "ƒ", "flag": "https://flagcdn.com/w320/cw.png" },
      { "code": "CYP", "name": "Cyprus", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/cy.png" },
      { "code": "CZE", "name": "Czech Republic", "currency": "CZK", "symbol": "Kč", "flag": "https://flagcdn.com/w320/cz.png" },
      { "code": "DNK", "name": "Denmark", "currency": "DKK", "symbol": "kr", "flag": "https://flagcdn.com/w320/dk.png" },
      { "code": "DJI", "name": "Djibouti", "currency": "DJF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/dj.png" },
      { "code": "DMA", "name": "Dominica", "currency": "XCD", "symbol": "$", "flag": "https://flagcdn.com/w320/dm.png" },
      { "code": "DOM", "name": "Dominican Republic", "currency": "DOP", "symbol": "$", "flag": "https://flagcdn.com/w320/do.png" },
      { "code": "ECU", "name": "Ecuador", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/ec.png" },
      { "code": "EGY", "name": "Egypt", "currency": "EGP", "symbol": "£", "flag": "https://flagcdn.com/w320/eg.png" },
      { "code": "SLV", "name": "El Salvador", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/sv.png" },
      { "code": "GNQ", "name": "Equatorial Guinea", "currency": "XAF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/gq.png" },
      { "code": "ERI", "name": "Eritrea", "currency": "ERN", "symbol": "Nfk", "flag": "https://flagcdn.com/w320/er.png" },
      { "code": "EST", "name": "Estonia", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/ee.png" },
      { "code": "ETH", "name": "Ethiopia", "currency": "ETB", "symbol": "Br", "flag": "https://flagcdn.com/w320/et.png" },
      { "code": "FLK", "name": "Falkland Islands (Malvinas)", "currency": "FKP", "symbol": "£", "flag": "https://flagcdn.com/w320/fk.png" },
      { "code": "FRO", "name": "Faroe Islands", "currency": "DKK", "symbol": "kr", "flag": "https://flagcdn.com/w320/fo.png" },
      { "code": "FJI", "name": "Fiji", "currency": "FJD", "symbol": "$", "flag": "https://flagcdn.com/w320/fj.png" },
      { "code": "FIN", "name": "Finland", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/fi.png" },
      { "code": "FRA", "name": "France", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/fr.png" },
      { "code": "GUF", "name": "French Guiana", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/gf.png" },
      { "code": "PYF", "name": "French Polynesia", "currency": "XPF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/pf.png" },
      { "code": "ATF", "name": "French Southern Territories", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/tf.png" },
      { "code": "GAB", "name": "Gabon", "currency": "XAF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/ga.png" },
      { "code": "GMB", "name": "Gambia", "currency": "GMD", "symbol": "D", "flag": "https://flagcdn.com/w320/gm.png" },
      { "code": "GEO", "name": "Georgia", "currency": "GEL", "symbol": "₾", "flag": "https://flagcdn.com/w320/ge.png" },
      { "code": "DEU", "name": "Germany", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/de.png" },
      { "code": "GHA", "name": "Ghana", "currency": "GHS", "symbol": "₵", "flag": "https://flagcdn.com/w320/gh.png" },
      { "code": "GIB", "name": "Gibraltar", "currency": "GIP", "symbol": "£", "flag": "https://flagcdn.com/w320/gi.png" },
      { "code": "GRC", "name": "Greece", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/gr.png" },
      { "code": "GRL", "name": "Greenland", "currency": "DKK", "symbol": "kr", "flag": "https://flagcdn.com/w320/gl.png" },
      { "code": "GRD", "name": "Grenada", "currency": "XCD", "symbol": "$", "flag": "https://flagcdn.com/w320/gd.png" },
      { "code": "GLP", "name": "Guadeloupe", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/gp.png" },
      { "code": "GUM", "name": "Guam", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/gu.png" },
      { "code": "GTM", "name": "Guatemala", "currency": "GTQ", "symbol": "Q", "flag": "https://flagcdn.com/w320/gt.png" },
      { "code": "GGY", "name": "Guernsey", "currency": "GBP", "symbol": "£", "flag": "https://flagcdn.com/w320/gg.png" },
      { "code": "GIN", "name": "Guinea", "currency": "GNF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/gn.png" },
      { "code": "GNB", "name": "Guinea-Bissau", "currency": "XOF", "symbol": "CFA", "flag": "https://flagcdn.com/w320/gw.png" },
      { "code": "GUY", "name": "Guyana", "currency": "GYD", "symbol": "$", "flag": "https://flagcdn.com/w320/gy.png" },
      { "code": "HTI", "name": "Haiti", "currency": "HTG", "symbol": "G", "flag": "https://flagcdn.com/w320/ht.png" },
      { "code": "HMD", "name": "Heard Island and McDonald Islands", "currency": "AUD", "symbol": "$", "flag": "https://flagcdn.com/w320/hm.png" },
      { "code": "VAT", "name": "Holy See", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/va.png" },
      { "code": "HND", "name": "Honduras", "currency": "HNL", "symbol": "L", "flag": "https://flagcdn.com/w320/hn.png" },
      { "code": "HKG", "name": "Hong Kong", "currency": "HKD", "symbol": "$", "flag": "https://flagcdn.com/w320/hk.png" },
      { "code": "HUN", "name": "Hungary", "currency": "HUF", "symbol": "Ft", "flag": "https://flagcdn.com/w320/hu.png" },
      { "code": "ISL", "name": "Iceland", "currency": "ISK", "symbol": "kr", "flag": "https://flagcdn.com/w320/is.png" },
      { "code": "IND", "name": "India", "currency": "INR", "symbol": "₹", "flag": "https://flagcdn.com/w320/in.png" },
      { "code": "IDN", "name": "Indonesia", "currency": "IDR", "symbol": "Rp", "flag": "https://flagcdn.com/w320/id.png" },
      { "code": "IRN", "name": "Iran (Islamic Republic of)", "currency": "IRR", "symbol": "﷼", "flag": "https://flagcdn.com/w320/ir.png" },
      { "code": "IRQ", "name": "Iraq", "currency": "IQD", "symbol": "ع.د", "flag": "https://flagcdn.com/w320/iq.png" },
      { "code": "IRL", "name": "Ireland", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/ie.png" },
      { "code": "IMN", "name": "Isle of Man", "currency": "GBP", "symbol": "£", "flag": "https://flagcdn.com/w320/im.png" },
      { "code": "ISR", "name": "Israel", "currency": "ILS", "symbol": "₪", "flag": "https://flagcdn.com/w320/il.png" },
      { "code": "ITA", "name": "Italy", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/it.png" },
      { "code": "JAM", "name": "Jamaica", "currency": "JMD", "symbol": "$", "flag": "https://flagcdn.com/w320/jm.png" },
      { "code": "JPN", "name": "Japan", "currency": "JPY", "symbol": "¥", "flag": "https://flagcdn.com/w320/jp.png" },
      { "code": "JEY", "name": "Jersey", "currency": "GBP", "symbol": "£", "flag": "https://flagcdn.com/w320/je.png" },
      { "code": "JOR", "name": "Jordan", "currency": "JOD", "symbol": "د.ا", "flag": "https://flagcdn.com/w320/jo.png" },
      { "code": "KAZ", "name": "Kazakhstan", "currency": "KZT", "symbol": "₸", "flag": "https://flagcdn.com/w320/kz.png" },
      { "code": "KEN", "name": "Kenya", "currency": "KES", "symbol": "Sh", "flag": "https://flagcdn.com/w320/ke.png" },
      { "code": "KIR", "name": "Kiribati", "currency": "AUD", "symbol": "$", "flag": "https://flagcdn.com/w320/ki.png" },
      { "code": "PRK", "name": "Korea ", "currency": "KPW", "symbol": "₩", "flag": "https://flagcdn.com/w320/kp.png" },
      { "code": "KOR", "name": "Korea (Republic of)", "currency": "KRW", "symbol": "₩", "flag": "https://flagcdn.com/w320/kr.png" },
      { "code": "KWT", "name": "Kuwait", "currency": "KWD", "symbol": "د.ك", "flag": "https://flagcdn.com/w320/kw.png" },
      { "code": "KGZ", "name": "Kyrgyzstan", "currency": "KGS", "symbol": "с", "flag": "https://flagcdn.com/w320/kg.png" },
      { "code": "LAO", "name": "Lao People's Democratic Republic", "currency": "LAK", "symbol": "₭", "flag": "https://flagcdn.com/w320/la.png" },
      { "code": "LVA", "name": "Latvia", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/lv.png" },
      { "code": "LBN", "name": "Lebanon", "currency": "LBP", "symbol": "ل.ل", "flag": "https://flagcdn.com/w320/lb.png" },
      { "code": "LSO", "name": "Lesotho", "currency": "LSL", "symbol": "L", "flag": "https://flagcdn.com/w320/ls.png" },
      { "code": "LBR", "name": "Liberia", "currency": "LRD", "symbol": "$", "flag": "https://flagcdn.com/w320/lr.png" },
      { "code": "LBY", "name": "Libya", "currency": "LYD", "symbol": "ل.د", "flag": "https://flagcdn.com/w320/ly.png" },
      { "code": "LIE", "name": "Liechtenstein", "currency": "CHF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/li.png" },
      { "code": "LTU", "name": "Lithuania", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/lt.png" },
      { "code": "LUX", "name": "Luxembourg", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/lu.png" },
      { "code": "MAC", "name": "Macao", "currency": "MOP", "symbol": "P", "flag": "https://flagcdn.com/w320/mo.png" },
      { "code": "MKD", "name": "Macedonia (the former Yugoslav Republic of)", "currency": "MKD", "symbol": "ден", "flag": "https://flagcdn.com/w320/mk.png" },
      { "code": "MDG", "name": "Madagascar", "currency": "MGA", "symbol": "Ar", "flag": "https://flagcdn.com/w320/mg.png" },
      { "code": "MWI", "name": "Malawi", "currency": "MWK", "symbol": "MK", "flag": "https://flagcdn.com/w320/mw.png" },
      { "code": "MYS", "name": "Malaysia", "currency": "MYR", "symbol": "RM", "flag": "https://flagcdn.com/w320/my.png" },
      { "code": "MDV", "name": "Maldives", "currency": "MVR", "symbol": ".ރ", "flag": "https://flagcdn.com/w320/mv.png" },
      { "code": "MLI", "name": "Mali", "currency": "XOF", "symbol": "CFA", "flag": "https://flagcdn.com/w320/ml.png" },
      { "code": "MLT", "name": "Malta", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/mt.png" },
      { "code": "MHL", "name": "Marshall Islands", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/mh.png" },
      { "code": "MTQ", "name": "Martinique", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/mq.png" },
      { "code": "MRT", "name": "Mauritania", "currency": "MRU", "symbol": "UM", "flag": "https://flagcdn.com/w320/mr.png" },
      { "code": "MUS", "name": "Mauritius", "currency": "MUR", "symbol": "₨", "flag": "https://flagcdn.com/w320/mu.png" },
      { "code": "MYT", "name": "Mayotte", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/yt.png" },
      { "code": "MEX", "name": "Mexico", "currency": "MXN", "symbol": "$", "flag": "https://flagcdn.com/w320/mx.png" },
      { "code": "FSM", "name": "Micronesia (Federated States of)", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/fm.png" },
      { "code": "MDA", "name": "Moldova (Republic of)", "currency": "MDL", "symbol": "L", "flag": "https://flagcdn.com/w320/md.png" },
      { "code": "MCO", "name": "Monaco", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/mc.png" },
      { "code": "MNG", "name": "Mongolia", "currency": "MNT", "symbol": "₮", "flag": "https://flagcdn.com/w320/mn.png" },
      { "code": "MNE", "name": "Montenegro", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/me.png" },
      { "code": "MSR", "name": "Montserrat", "currency": "XCD", "symbol": "$", "flag": "https://flagcdn.com/w320/ms.png" },
      { "code": "MAR", "name": "Morocco", "currency": "MAD", "symbol": "د.م.", "flag": "https://flagcdn.com/w320/ma.png" },
      { "code": "MOZ", "name": "Mozambique", "currency": "MZN", "symbol": "MT", "flag": "https://flagcdn.com/w320/mz.png" },
      { "code": "MMR", "name": "Myanmar", "currency": "MMK", "symbol": "Ks", "flag": "https://flagcdn.com/w320/mm.png" },
      { "code": "NAM", "name": "Namibia", "currency": "NAD", "symbol": "$", "flag": "https://flagcdn.com/w320/na.png" },
      { "code": "NRU", "name": "Nauru", "currency": "AUD", "symbol": "$", "flag": "https://flagcdn.com/w320/nr.png" },
      { "code": "NPL", "name": "Nepal", "currency": "NPR", "symbol": "₨", "flag": "https://flagcdn.com/w320/np.png" },
      { "code": "NLD", "name": "Netherlands", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/nl.png" },
      { "code": "NCL", "name": "New Caledonia", "currency": "XPF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/nc.png" },
      { "code": "NZL", "name": "New Zealand", "currency": "NZD", "symbol": "$", "flag": "https://flagcdn.com/w320/nz.png" },
      { "code": "NIC", "name": "Nicaragua", "currency": "NIO", "symbol": "C$", "flag": "https://flagcdn.com/w320/ni.png" },
      { "code": "NER", "name": "Niger", "currency": "XOF", "symbol": "CFA", "flag": "https://flagcdn.com/w320/ne.png" },
      { "code": "NGA", "name": "Nigeria", "currency": "NGN", "symbol": "₦", "flag": "https://flagcdn.com/w320/ng.png" },
      { "code": "NIU", "name": "Niue", "currency": "NZD", "symbol": "$", "flag": "https://flagcdn.com/w320/nu.png" },
      { "code": "NFK", "name": "Norfolk Island", "currency": "AUD", "symbol": "$", "flag": "https://flagcdn.com/w320/nf.png" },
      { "code": "MNP", "name": "Northern Mariana Islands", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/mp.png" },
      { "code": "NOR", "name": "Norway", "currency": "NOK", "symbol": "kr", "flag": "https://flagcdn.com/w320/no.png" },
      { "code": "OMN", "name": "Oman", "currency": "OMR", "symbol": "ر.ع.", "flag": "https://flagcdn.com/w320/om.png" },
      { "code": "PAK", "name": "Pakistan", "currency": "PKR", "symbol": "₨", "flag": "https://flagcdn.com/w320/pk.png" },
      { "code": "PLW", "name": "Palau", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/pw.png" },
      { "code": "PSE", "name": "Palestine, State of", "currency": "ILS", "symbol": "₪", "flag": "https://flagcdn.com/w320/ps.png" },
      { "code": "PAN", "name": "Panama", "currency": "PAB", "symbol": "B/.", "flag": "https://flagcdn.com/w320/pa.png" },
      { "code": "PNG", "name": "Papua New Guinea", "currency": "PGK", "symbol": "K", "flag": "https://flagcdn.com/w320/pg.png" },
      { "code": "PRY", "name": "Paraguay", "currency": "PYG", "symbol": "₲", "flag": "https://flagcdn.com/w320/py.png" },
      { "code": "PER", "name": "Peru", "currency": "PEN", "symbol": "S/.", "flag": "https://flagcdn.com/w320/pe.png" },
      { "code": "PHL", "name": "Philippines", "currency": "PHP", "symbol": "₱", "flag": "https://flagcdn.com/w320/ph.png" },
      { "code": "PCN", "name": "Pitcairn", "currency": "NZD", "symbol": "$", "flag": "https://flagcdn.com/w320/pn.png" },
      { "code": "POL", "name": "Poland", "currency": "PLN", "symbol": "zł", "flag": "https://flagcdn.com/w320/pl.png" },
      { "code": "PRT", "name": "Portugal", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/pt.png" },
      { "code": "PRI", "name": "Puerto Rico", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/pr.png" },
      { "code": "QAT", "name": "Qatar", "currency": "QAR", "symbol": "ر.ق", "flag": "https://flagcdn.com/w320/qa.png" },
      { "code": "REU", "name": "Réunion", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/re.png" },
      { "code": "ROU", "name": "Romania", "currency": "RON", "symbol": "lei", "flag": "https://flagcdn.com/w320/ro.png" },
      { "code": "RUS", "name": "Russian Federation", "currency": "RUB", "symbol": "₽", "flag": "https://flagcdn.com/w320/ru.png" },
      { "code": "RWA", "name": "Rwanda", "currency": "RWF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/rw.png" },
      { "code": "BLM", "name": "Saint Barthélemy", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/bl.png" },
      { "code": "SHN", "name": "Saint Helena", "currency": "SHP", "symbol": "£", "flag": "https://flagcdn.com/w320/sh.png" },
      { "code": "KNA", "name": "Saint Kitts and Nevis", "currency": "XCD", "symbol": "$", "flag": "https://flagcdn.com/w320/kn.png" },
      { "code": "LCA", "name": "Saint Lucia", "currency": "XCD", "symbol": "$", "flag": "https://flagcdn.com/w320/lc.png" },
      { "code": "MAF", "name": "Saint Martin (French part)", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/mf.png" },
      { "code": "SPM", "name": "Saint Pierre and Miquelon", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/pm.png" },
      { "code": "VCT", "name": "Saint Vincent and the Grenadines", "currency": "XCD", "symbol": "$", "flag": "https://flagcdn.com/w320/vc.png" },
      { "code": "WSM", "name": "Samoa", "currency": "WST", "symbol": "T", "flag": "https://flagcdn.com/w320/ws.png" },
      { "code": "SMR", "name": "San Marino", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/sm.png" },
      { "code": "STP", "name": "Sao Tome and Principe", "currency": "STN", "symbol": "Db", "flag": "https://flagcdn.com/w320/st.png" },
      { "code": "SAU", "name": "Saudi Arabia", "currency": "SAR", "symbol": "ر.س", "flag": "https://flagcdn.com/w320/sa.png" },
      { "code": "SEN", "name": "Senegal", "currency": "XOF", "symbol": "CFA", "flag": "https://flagcdn.com/w320/sn.png" },
      { "code": "SRB", "name": "Serbia", "currency": "RSD", "symbol": "din", "flag": "https://flagcdn.com/w320/rs.png" },
      { "code": "SYC", "name": "Seychelles", "currency": "SCR", "symbol": "₨", "flag": "https://flagcdn.com/w320/sc.png" },
      { "code": "SLE", "name": "Sierra Leone", "currency": "SLL", "symbol": "Le", "flag": "https://flagcdn.com/w320/sl.png" },
      { "code": "SGP", "name": "Singapore", "currency": "SGD", "symbol": "$", "flag": "https://flagcdn.com/w320/sg.png" },
      { "code": "SXM", "name": "Sint Maarten (Dutch part)", "currency": "ANG", "symbol": "ƒ", "flag": "https://flagcdn.com/w320/sx.png" },
      { "code": "SVK", "name": "Slovakia", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/sk.png" },
      { "code": "SVN", "name": "Slovenia", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/si.png" },
      { "code": "SLB", "name": "Solomon Islands", "currency": "SBD", "symbol": "$", "flag": "https://flagcdn.com/w320/sb.png" },
      { "code": "SOM", "name": "Somalia", "currency": "SOS", "symbol": "Sh", "flag": "https://flagcdn.com/w320/so.png" },
      { "code": "ZAF", "name": "South Africa", "currency": "ZAR", "symbol": "R", "flag": "https://flagcdn.com/w320/za.png" },
      { "code": "SGS", "name": "South Georgia and the South Sandwich Islands", "currency": "GBP", "symbol": "£", "flag": "https://flagcdn.com/w320/gs.png" },
      { "code": "SSD", "name": "South Sudan", "currency": "SSP", "symbol": "£", "flag": "https://flagcdn.com/w320/ss.png" },
      { "code": "ESP", "name": "Spain", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/es.png" },
      { "code": "LKA", "name": "Sri Lanka", "currency": "LKR", "symbol": "₨", "flag": "https://flagcdn.com/w320/lk.png" },
      { "code": "SDN", "name": "Sudan", "currency": "SDG", "symbol": "ج.س.", "flag": "https://flagcdn.com/w320/sd.png" },
      { "code": "SUR", "name": "Suriname", "currency": "SRD", "symbol": "$", "flag": "https://flagcdn.com/w320/sr.png" },
      { "code": "SJM", "name": "Svalbard and Jan Mayen", "currency": "NOK", "symbol": "kr", "flag": "https://flagcdn.com/w320/sj.png" },
      { "code": "SWZ", "name": "Eswatini", "currency": "SZL", "symbol": "E", "flag": "https://flagcdn.com/w320/sz.png" },
      { "code": "SWE", "name": "Sweden", "currency": "SEK", "symbol": "kr", "flag": "https://flagcdn.com/w320/se.png" },
      { "code": "CHE", "name": "Switzerland", "currency": "CHF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/ch.png" },
      { "code": "SYR", "name": "Syrian Arab Republic", "currency": "SYP", "symbol": "£", "flag": "https://flagcdn.com/w320/sy.png" },
      { "code": "TWN", "name": "Taiwan, Province of China", "currency": "TWD", "symbol": "$", "flag": "https://flagcdn.com/w320/tw.png" },
      { "code": "TJK", "name": "Tajikistan", "currency": "TJS", "symbol": "ЅМ", "flag": "https://flagcdn.com/w320/tj.png" },
      { "code": "TZA", "name": "Tanzania, United Republic of", "currency": "TZS", "symbol": "Sh", "flag": "https://flagcdn.com/w320/tz.png" },
      { "code": "THA", "name": "Thailand", "currency": "THB", "symbol": "฿", "flag": "https://flagcdn.com/w320/th.png" },
      { "code": "TLS", "name": "Timor-Leste", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/tl.png" },
      { "code": "TGO", "name": "Togo", "currency": "XOF", "symbol": "CFA", "flag": "https://flagcdn.com/w320/tg.png" },
      { "code": "TKL", "name": "Tokelau", "currency": "NZD", "symbol": "$", "flag": "https://flagcdn.com/w320/tk.png" },
      { "code": "TON", "name": "Tonga", "currency": "TOP", "symbol": "T$", "flag": "https://flagcdn.com/w320/to.png" },
      { "code": "TTO", "name": "Trinidad and Tobago", "currency": "TTD", "symbol": "$", "flag": "https://flagcdn.com/w320/tt.png" },
      { "code": "TUN", "name": "Tunisia", "currency": "TND", "symbol": "د.ت", "flag": "https://flagcdn.com/w320/tn.png" },
      { "code": "TUR", "name": "Turkey", "currency": "TRY", "symbol": "₺", "flag": "https://flagcdn.com/w320/tr.png" },
      { "code": "TKM", "name": "Turkmenistan", "currency": "TMT", "symbol": "m", "flag": "https://flagcdn.com/w320/tm.png" },
      { "code": "TCA", "name": "Turks and Caicos Islands", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/tc.png" },
      { "code": "TUV", "name": "Tuvalu", "currency": "AUD", "symbol": "$", "flag": "https://flagcdn.com/w320/tv.png" },
      { "code": "UGA", "name": "Uganda", "currency": "UGX", "symbol": "Sh", "flag": "https://flagcdn.com/w320/ug.png" },
      { "code": "UKR", "name": "Ukraine", "currency": "UAH", "symbol": "₴", "flag": "https://flagcdn.com/w320/ua.png" },
      { "code": "ARE", "name": "United Arab Emirates", "currency": "AED", "symbol": "د.إ", "flag": "https://flagcdn.com/w320/ae.png" },
      { "code": "GBR", "name": "United Kingdom ", "currency": "GBP", "symbol": "£", "flag": "https://flagcdn.com/w320/gb.png" },
      { "code": "UMI", "name": "United States ", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/um.png" },
      { "code": "URY", "name": "Uruguay", "currency": "UYU", "symbol": "$", "flag": "https://flagcdn.com/w320/uy.png" },
      { "code": "UZB", "name": "Uzbekistan", "currency": "UZS", "symbol": "лв", "flag": "https://flagcdn.com/w320/uz.png" },
      { "code": "VUT", "name": "Vanuatu", "currency": "VUV", "symbol": "Vt", "flag": "https://flagcdn.com/w320/vu.png" },
      { "code": "VEN", "name": "Venezuela (Bolivarian Republic of)", "currency": "VES", "symbol": "Bs.S", "flag": "https://flagcdn.com/w320/ve.png" },
      { "code": "VNM", "name": "Viet Nam", "currency": "VND", "symbol": "₫", "flag": "https://flagcdn.com/w320/vn.png" },
      { "code": "VGB", "name": "Virgin Islands (British)", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/vg.png" },
      { "code": "VIR", "name": "Virgin Islands (U.S.)", "currency": "USD", "symbol": "$", "flag": "https://flagcdn.com/w320/vi.png" },
      { "code": "WLF", "name": "Wallis and Futuna", "currency": "XPF", "symbol": "Fr", "flag": "https://flagcdn.com/w320/wf.png" },
      { "code": "ESH", "name": "Western Sahara", "currency": "MAD", "symbol": "د.م.", "flag": "https://flagcdn.com/w320/eh.png" },
      { "code": "YEM", "name": "Yemen", "currency": "YER", "symbol": "﷼", "flag": "https://flagcdn.com/w320/ye.png" },
      { "code": "ZMB", "name": "Zambia", "currency": "ZMW", "symbol": "ZK", "flag": "https://flagcdn.com/w320/zm.png" },
      { "code": "ZWE", "name": "Zimbabwe", "currency": "ZWL", "symbol": "$", "flag": "https://flagcdn.com/w320/zw.png" },
      { "code": "ALA", "name": "Åland Islands", "currency": "EUR", "symbol": "€", "flag": "https://flagcdn.com/w320/ax.png" },
    
   
  ];

  const fetchCountries = async () => {
    try {
      // First, try to fetch from RestCountries API
      const res = await axios.get('https://restcountries.com/v3.1/all');
      
      if (res.data && res.data.length > 100) {
        const countriesData: Country[] = res.data.map((country) => ({
          code: country.cca3 || country.ccn3 || '',
          name: country.name?.common || country.name || '',
          currency: country.currencies 
            ? Object.keys(country.currencies)[0] 
            : '',
          symbol: country.currencies 
            ? Object.values(country.currencies)[0]?.symbol || '$'
            : '$',
          flag: country.flags?.png || `https://flagcdn.com/w320/${country.cca2?.toLowerCase()}.png` || '',
        })).filter(country => country.code && country.name && country.flag);

        return countriesData.length > 0 ? countriesData : COUNTRIES_LIST;
      }
      
      // Fallback to hardcoded list
      return COUNTRIES_LIST;
    } catch (error) {
      console.error('Error fetching countries:', error);
      return COUNTRIES_LIST;
    }
  };

  useEffect(() => {
    const loadCountries = async () => {
      setHasTriedLoading(true);
      setCountriesLoading(true);
      try {
        const fetchedCountries = await fetchCountries();
        
        console.log('Loaded Countries:', fetchedCountries);
        
        if (!fetchedCountries || fetchedCountries.length === 0) {
          setError('No countries found. Please check your network connection.');
          console.error('No countries loaded');
        } else {
          setCountries(fetchedCountries);
          
          // Try to restore previously selected countries from localStorage
          const storedCountry1Code = localStorage.getItem('country1Code');
          const storedCountry2Code = localStorage.getItem('country2Code');

          if (storedCountry1Code) {
            const storedCountry1 = fetchedCountries.find((c) => c.code === storedCountry1Code);
            if (storedCountry1) setCountry1(storedCountry1);
          }

          if (storedCountry2Code) {
            const storedCountry2 = fetchedCountries.find((c) => c.code === storedCountry2Code);
            if (storedCountry2) setCountry2(storedCountry2);
          }
        }
      } catch (err) {
        console.error('Error in loadCountries:', err);
        setError('Failed to load countries. Please try again later.');
      } finally {
        setCountriesLoading(false);
      }
    };
    
    loadCountries();
  }, []);

  const fetchPPPData = async (countryCode: string | undefined) => {
    if (!countryCode) return null;
    try {
      const currentYear = new Date().getFullYear();
      const apiUrl = `https://api.worldbank.org/v2/country/${countryCode}/indicator/PA.NUS.PPP?format=json&per_page=100&date=${currentYear - 5}:${currentYear}`;
      const response = await axios.get(apiUrl);

      const extractLatestPPP = (data: any) => {
        if (data && data[1] && Array.isArray(data[1])) {
          const validDataPoints = data[1].filter((item: any) => item.value !== null && item.date);
          if (validDataPoints.length > 0) {
            const latestDataPoint = validDataPoints.reduce((prev: any, curr: any) => (prev.date > curr.date) ? prev : curr);
            return latestDataPoint.value;
          }
        }
        return null;
      };

      return extractLatestPPP(response.data);

    } catch (error) {
      console.error(`Error fetching PPP data for ${countryCode}:`, error);
      return null;
    }
  };

  const calculateInflationAdjustedValue = (amount: number, startYear: string, endYear: string) => {
    const startYearNum = parseInt(startYear);
    const endYearNum = parseInt(endYear);

    if (startYearNum === endYearNum) return amount;

    let adjustedValue = amount;

    if (startYearNum < endYearNum) {
      for (let year = startYearNum + 1; year <= endYearNum; year++) {
        const inflationRate = inflationData[year.toString()] ||
          (year < new Date().getFullYear() ? 2.5 : 0);
        adjustedValue *= (1 + inflationRate / 100);
      }
    } else {
      for (let year = startYearNum; year > endYearNum; year--) {
        const inflationRate = inflationData[year.toString()] ||
          (year < new Date().getFullYear() ? 2.5 : 0);
        adjustedValue /= (1 + inflationRate / 100);
      }
    }

    return adjustedValue;
  };

  const calculateResult = async () => {
    if (!amount) {
      setAmountError('Please enter an amount');
      return;
    }

    if (parseFloat(amount) <= 0) {
      setAmountError('Amount must be greater than 0');
      return;
    }

    if (!country1) {
      setError('Please select a country.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const numAmount = parseFloat(amount) || 0;

      if (calculatorType === 'ppp') {
        if (!country2) {
          throw new Error('Please select both countries.');
        }

        const ppp1 = await fetchPPPData(country1.code);
        const ppp2 = await fetchPPPData(country2.code);

        if (!ppp1 || !ppp2) {
          throw new Error('PPP data is unavailable for one or both countries.');
        }

        const convertedAmount = (numAmount * ppp2) / ppp1;
        setResult({ amount: convertedAmount, fromAmount: numAmount });
      } else {
        if (!referenceYear || !targetYear) {
          throw new Error('Please select both years.');
        }

        const adjustedAmount = calculateInflationAdjustedValue(
          numAmount,
          referenceYear,
          targetYear
        );

        setResult({ amount: adjustedAmount, fromAmount: numAmount });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while calculating.');
      setResult({ amount: 0, fromAmount: 0 });
    } finally {
      setLoading(false);
    }
  };

  const fetchCountryInflationData = async (countryCode: string) => {
    setInflationDataLoading(true);
    setUsingDefaultInflationData(false);
    try {
      const response = await axios.get(
        `https://api.worldbank.org/v2/country/${countryCode}/indicator/FP.CPI.TOTL.ZG?format=json&per_page=100`
      );

      if (response.data && response.data[1]) {
        const rates: Record<string, number> = {};
        response.data[1].forEach((item: any) => {
          if (item.date && item.value) {
            rates[item.date] = item.value;
          }
        });

        if (Object.keys(rates).length > 0) {
          setInflationData(rates);
        } else {
          setInflationData(getDefaultInflationData());
          setUsingDefaultInflationData(true);
        }
      } else {
        setInflationData(getDefaultInflationData());
        setUsingDefaultInflationData(true);
      }
    } catch (error) {
      console.error('Error fetching inflation data:', error);
      setInflationData(getDefaultInflationData());
      setUsingDefaultInflationData(true);
    } finally {
      setInflationDataLoading(false);
    }
  };

  const getDefaultInflationData = (): Record<string, number> => ({
    '1900': 1.2,
    '1901': 1.1,
    '1902': 2.1,
    '1903': 1.9,
    '1904': -0.9,
    '1905': 2.3,
    '1906': 1.8,
    '1907': 2.0,
    '1908': -1.0,
    '1909': 0.5,
    '1910': 0.9,
    '1911': 2.1,
    '1912': 1.9,
    '1913': 2.0,
    '1914': 1.0,
    '1915': 0.9,
    '1916': 7.6,
    '1917': 17.8,
    '1918': 18.0,
    '1919': 14.6,
    '1920': 15.6,
    '1921': -10.5,
    '1922': -6.1,
    '1923': 1.8,
    '1924': 0.4,
    '1925': 2.4,
    '1926': 1.0,
    '1927': -1.7,
    '1928': -1.2,
    '1929': 0.0,
    '1930': -2.3,
    '1931': -8.9,
    '1932': -10.3,
    '1933': -5.1,
    '1934': 3.1,
    '1935': 2.2,
    '1936': 1.0,
    '1937': 3.6,
    '1938': -2.1,
    '1939': -1.4,
    '1940': 0.7,
    '1941': 5.1,
    '1942': 10.9,
    '1943': 6.0,
    '1944': 1.6,
    '1945': 2.3,
    '1946': 8.5,
    '1947': 14.4,
    '1948': 7.7,
    '1949': -1.0,
    '1950': 1.1,
    '1951': 7.9,
    '1952': 2.3,
    '1953': 0.8,
    '1954': 0.3,
    '1955': -0.4,
    '1956': 1.5,
    '1957': 3.3,
    '1958': 2.8,
    '1959': 1.7,
    '1960': 1.5,
    '1961': 1.1,
    '1962': 1.0,
    '1963': 1.3,
    '1964': 1.3,
    '1965': 1.6,
    '1966': 3.0,
    '1967': 3.1,
    '1968': 4.2,
    '1969': 5.5,
    '1970': 5.8,
    '1971': 4.3,
    '1972': 3.3,
    '1973': 6.2,
    '1974': 11.0,
    '1975': 9.1,
    '1976': 5.7,
    '1977': 6.5,
    '1978': 7.6,
    '1979': 11.3,
    '1980': 13.5,
    '1981': 10.3,
    '1982': 6.1,
    '1983': 3.2,
    '1984': 4.3,
    '1985': 3.6,
    '1986': 1.9,
    '1987': 3.6,
    '1988': 4.1,
    '1989': 4.8,
    '1990': 5.4,
    '1991': 4.2,
    '1992': 3.0,
    '1993': 2.9,
    '1994': 2.6,
    '1995': 2.8,
    '1996': 3.0,
    '1997': 2.3,
    '1998': 1.6,
    '1999': 2.2,
    '2000': 3.4,
    '2001': 2.8,
    '2002': 1.6,
    '2003': 2.3,
    '2004': 2.7,
    '2005': 3.4,
    '2006': 3.2,
    '2007': 2.8,
    '2008': 3.8,
    '2009': -0.4,
    '2010': 1.64,
    '2011': 3.16,
    '2012': 2.07,
    '2013': 1.47,
    '2014': 1.62,
    '2015': 0.12,
    '2016': 1.26,
    '2017': 2.13,
    '2018': 2.44,
    '2019': 1.81,
    '2020': 1.23,
    '2021': 4.70,
    '2022': 8.00,
    '2023': 4.10,
    '2024': 4.5,
    '2025': 4.7
  });

  useEffect(() => {
    if (country1 && calculatorType === 'buyingPower') {
      fetchCountryInflationData(country1.code);
    }
  }, [country1, calculatorType]);

  useEffect(() => {
    // Only trigger calculation if we have both countries and an amount
    // Removed automatic calculation
  }, [country1, country2, amount]);

  useEffect(() => {
    localStorage.setItem('amount', amount);
    localStorage.setItem('result', JSON.stringify(result));
    localStorage.setItem('referenceYear', referenceYear);
    localStorage.setItem('targetYear', targetYear);
    if (country1) {
      localStorage.setItem('country1Code', country1.code);
    }
    if (country2) {
      localStorage.setItem('country2Code', country2.code);
    }
  }, [amount, result, referenceYear, targetYear, country1, country2]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdown1Ref.current && !dropdown1Ref.current.contains(event.target as Node)) {
        setShowDropdown1(false);
      }
      if (dropdown2Ref.current && !dropdown2Ref.current.contains(event.target as Node)) {
        setShowDropdown2(false);
      }
      if (referenceYearDropdownRef.current && !referenceYearDropdownRef.current.contains(event.target as Node)) {
        setShowReferenceYearDropdown(false);
      }
      if (targetYearDropdownRef.current && !targetYearDropdownRef.current.contains(event.target as Node)) {
        setShowTargetYearDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const years = Array.from({ length: new Date().getFullYear() - 1900 + 1 }, (_, i) => String(1900 + i)).reverse();

  const [countryFilter, setCountryFilter] = useState('');

  const filteredCountries = useMemo(() => {
    if (!countries) return [];
    return countries.filter(country => 
      country.name.toLowerCase().includes(countryFilter.toLowerCase())
    );
  }, [countries, countryFilter]);

  return (
    <div>
      <div className="min-h-screen bg-gradient-to-r from-[#EDEDED] to-[#D9D9D9] rounded-b-3xl">
        <div className="max-w-5xl mx-auto px-4 py-8 md:py-16 relative">
          <img
            src={`${process.env.PUBLIC_URL}/Group 2 (1).png`}
            alt=""
            className="absolute hidden lg:block 
                      top-1/2 
                      lg:right-[-80px] xl:right-[-145px] 
                      lg:h-[250px] xl:h-[422px] 
                      -translate-y-1/2 
                      origin-center"
          />

          <img
            src={`${process.env.PUBLIC_URL}/Group 3.png`}
            alt=""
            className="absolute hidden lg:block 
                      top-1/2 
                      lg:left-[-80px] xl:left-[-154px] 
                      lg:h-[250px] xl:h-[422px] 
                      -translate-y-1/2 
                      origin-center"
          />

          <div className="relative p-[13px] overflow-hidden">
            <div className="absolute inset-0 border-[13px] border-transparent rounded-2xl backdrop-blur-sm"></div>
            <div className="relative bg-white rounded-xl shadow-lg p-6 md:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12">
                <div className="flex flex-col justify-between">
                  <div>
                    <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-6 mb-6 md:mb-8">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          checked={calculatorType === 'ppp'}
                          onChange={() => setCalculatorType('ppp')}
                          className="form-radio h-5 w-5 accent-black"
                        />
                        <span className="ml-2 text-lg">PPP Calculator</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          checked={calculatorType === 'buyingPower'}
                          onChange={() => setCalculatorType('buyingPower')}
                          className="form-radio h-5 w-5 accent-black"
                        />
                        <span className="ml-2 text-lg">Buying Power Calculator</span>
                      </label>
                    </div>

                    <div className="space-y-4 md:space-y-6">
                      <div>
                        <label className="block text-base font-medium text-[#252525] mb-2 text-left"> Source Country</label>
                        <div className="relative">
                          <div
                            onClick={() => setShowDropdown1(!showDropdown1)}
                            className={`pl-12 w-full border border-gray-300 rounded-xl px-4 py-3 bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent cursor-pointer text-left`}
                          >
                            {country1?.name || 'Select country'}
                          </div>
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            {country1?.flag ? (
                              <img
                                src={country1.flag}
                                alt={country1.code}
                                className="w-[21px] h-[16px]"
                                style={{ objectFit: 'cover' }}
                              />
                            ) : (
                              <div className="w-[21px] h-[16px] bg-gray-200"></div>
                            )}
                          </div>
                          {showDropdown1 && (
                            <div ref={dropdown1Ref} className="absolute mt-1 w-full bg-white rounded-md shadow-lg z-10 max-h-48 overflow-y-auto">
                              <input 
                                type="text" 
                                placeholder="Search countries..." 
                                value={countryFilter}
                                onChange={(e) => setCountryFilter(e.target.value)}
                                className="w-full p-2 mb-2 border rounded"
                              />
                              {filteredCountries.map((country) => (
                                <div 
                                  key={country.code} 
                                  onClick={() => {
                                    setCountry1(country);
                                    setCountryFilter('');
                                    setShowDropdown1(false);
                                  }}
                                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                                >
                                  <img 
                                    src={country.flag} 
                                    alt={`${country.name} flag`} 
                                    className="w-8 h-5 mr-2 object-cover" 
                                  />
                                  <span>{country.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-base font-medium text-[#252525] mb-2 text-left">Enter amount</label>
                        <div className="flex items-center">
                          <div className="relative flex-grow">
                            <div className="absolute inset-y-0 left-0 px-3 flex items-center pointer-events-none border-r border-gray-300">
                              <span className="text-gray-500 text-base">
                                {country1?.symbol || '$'}
                              </span>
                            </div>
                            <input
                              type="text"
                              value={amount}
                              onChange={handleAmountChange}
                              className="w-full border border-gray-300 rounded-xl px-[45px] py-3 text-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              placeholder="Enter amount"
                              required
                            />
                          </div>
                          {calculatorType === 'ppp' && (
                            <button
                              onClick={swapCountries}
                              className="ml-2 px-2 flex items-center justify-center rounded-xl border border-green-500 w-14 h-14 hover:bg-green-100 transition-colors"
                              title="Swap countries"
                            >
                              <svg className="h-6 w-6" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                  d="M4.66667 3.16667V13.8333M4.66667 13.8333L2 11.1667M4.66667 13.8333L7.33333 11.1667M11.3333 13.8333V3.16667M11.3333 3.16667L8.66667 5.83333M11.3333 3.16667L14 5.83333"
                                  stroke="url(#paint0_linear_53_20601)"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <defs>
                                  <linearGradient id="paint0_linear_53_20601" x1="8" y1="3.16667" x2="8" y2="13.8333" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#5AC57B" />
                                    <stop offset="1" stopColor="#15B146" />
                                  </linearGradient>
                                </defs>
                              </svg>
                            </button>
                          )}
                        </div>
                        {amountError && (
                          <p className="text-red-500 text-sm mt-1">{amountError}</p>
                        )}
                      </div>

                      {calculatorType === 'ppp' ? (
                        <div>
                          <label className="block text-base font-medium text-[#252525] mb-2 text-left">
                          Targeted Country
                           </label>
                          <div className="relative">
                            <div
                              onClick={() => setShowDropdown2(!showDropdown2)}
                              className={`pl-12 w-full border border-gray-300 rounded-xl px-4 py-3 bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent cursor-pointer text-left`}
                            >
                              {country2?.name || 'Select country'}
                            </div>
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              {country2?.flag ? (
                                <img
                                  src={country2.flag}
                                  alt={country2.code}
                                  className="w-[21px] h-[16px]"
                                  style={{ objectFit: 'cover' }}
                                />
                              ) : (
                                <div className="w-[21px] h-[16px] bg-gray-200"></div>
                              )}
                            </div>
                            {showDropdown2 && (
                              <div ref={dropdown2Ref} className="absolute mt-1 w-full bg-white rounded-md shadow-lg z-10 max-h-48 overflow-y-auto">
                                <input 
                                  type="text" 
                                  placeholder="Search countries..." 
                                  value={countryFilter}
                                  onChange={(e) => setCountryFilter(e.target.value)}
                                  className="w-full p-2 mb-2 border rounded"
                                />
                                {filteredCountries.map((country) => (
                                  <div 
                                    key={country.code} 
                                    onClick={() => {
                                      setCountry2(country);
                                      setCountryFilter('');
                                      setShowDropdown2(false);
                                    }}
                                    className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                                  >
                                    <img 
                                      src={country.flag} 
                                      alt={`${country.name} flag`} 
                                      className="w-8 h-5 mr-2 object-cover" 
                                    />
                                    <span>{country.name}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div>
                            <label className="block text-base font-medium text-[#252525] mb-2 text-left">Reference Year</label>
                            <div className="relative">
                              <div
                                onClick={() => setShowReferenceYearDropdown(!showReferenceYearDropdown)}
                                className="pl-8 w-full border border-gray-300 rounded-xl pr-6 pl-[15px] py-3 text-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent cursor-pointer text-left"
                              >
                                {referenceYear}
                              </div>
                              {showReferenceYearDropdown && (
                                <div ref={referenceYearDropdownRef} className="absolute mt-1 w-full bg-white rounded-md shadow-lg z-10 max-h-48 overflow-y-auto">
                                  {years.map((year) => (
                                    <div
                                      key={year}
                                      onClick={() => {
                                        setReferenceYear(year);
                                        setShowReferenceYearDropdown(false);
                                      }}
                                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                    >
                                      {year}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className='pt-[18px]'>
                            <label className="block text-base font-medium text-[#252525] mb-2 text-left">Target Year</label>
                            <div className="relative">
                              <div
                                onClick={() => setShowTargetYearDropdown(!showTargetYearDropdown)}
                                className="pl-8 w-full border border-gray-300 rounded-xl pr-6 pl-[15px] py-3 text-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent cursor-pointer text-left"
                              >
                                {targetYear}
                              </div>
                              {showTargetYearDropdown && (
                                <div ref={targetYearDropdownRef} className="absolute mt-1 w-full bg-white rounded-md shadow-lg z-10 max-h-48 overflow-y-auto">
                                  {years.map((year) => (
                                    <div
                                      key={year}
                                      onClick={() => {
                                        setTargetYear(year);
                                        setShowTargetYearDropdown(false);
                                      }}
                                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                    >
                                      {year}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-auto">
                    <button 
                      type="button"
                      onClick={() => {
                        setFinalCountry1(country1);
                        setFinalCountry2(country2);
                        setFinalAmount(amount);
                        calculateResult();
                      }}
                      disabled={!country1 || !country2 || !amount}
                      className={`w-full py-3 rounded-lg text-white font-semibold transition-colors duration-300 ${
                        !country1 || !country2 || !amount 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-green-600 hover:bg-green-700 active:bg-green-800'
                      }`}
                    >
                      {loading ? 'Calculating...' : 'Calculate'}
                      {!loading && (
                        <svg className="ml-2 inline-block" width="18" height="18" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4.66663 11.8334L11.3333 5.16675" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M4.66663 5.16675H11.3333V11.8334" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="border-2 border-dashed border-green-200 rounded-[20px] flex flex-col items-center justify-center p-6 md:p-8 min-h-[300px] md:min-h-[550px]">
                  {error && (
                    <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-2 rounded-lg mb-4 text-sm text-center">
                      {error}
                    </div>
                  )}
                  {usingDefaultInflationData && calculatorType === 'buyingPower' && (
                    <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-2 rounded-lg mb-4 text-sm text-center">
                      Using default inflation rates for {country1?.name || 'selected country'}
                    </div>
                  )}
                  {(inflationDataLoading || countriesLoading) && (
                    <div className="mb-4 text-sm text-center">
                      Loading data...
                    </div>
                  )}
                  <div className="flex flex-col items-center justify-center flex-grow">
                    <>
                      <h3 className="text-2xl font-medium text-[#15B146] text-center">
                        {calculatorType === 'ppp' ? finalCountry2?.symbol || '$' : finalCountry1?.symbol || '$'}
                        {result.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </h3>
                      <p className="text-[#252525] text-base mb-4 text-center">
                        ({finalCountry1?.symbol || '$'}{result.fromAmount.toLocaleString()})
                      </p>
                      <div className="text-center text-base text-[#252525] leading-relaxed flex flex-col justify-center">
                        <p className="mb-4 md:mb-6 md:px-[50px] px-0">
                          {calculatorType === 'ppp' ? (
                            <>
                              <span className="font-bold font-inter">{finalCountry1?.symbol || '$'}{result.fromAmount.toLocaleString()}</span> in the "{finalCountry1?.name || 'Country 1'}"
                              <span> has the same Purchasing Power as </span>
                              <span className="font-bold font-inter">{finalCountry2?.symbol || '$'}{result.amount.toLocaleString()}</span> in "{finalCountry2?.name || 'Country 2'}".
                            </>
                          ) : (
                            <>
                              <span className="font-bold font-inter">{finalCountry1?.symbol || '$'}{result.fromAmount.toLocaleString()}</span> in {referenceYear}
                              <span> has the same buying power as </span>
                              <span className="font-bold font-inter">{finalCountry1?.symbol || '$'}{result.amount.toLocaleString()}</span> in {targetYear}.
                            </>
                          )}
                        </p>

                        {calculatorType === 'buyingPower' && (
                          <p className="mb-4 md:mb-6">
                            adjusted for inflation from {referenceYear} to {targetYear}.
                          </p>
                        )}
                      </div>
                    </>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {calculatorType === 'ppp' && <PurchasingPowerParity />}
      {/* {calculatorType === 'buyingPower' && <BuyingPowerIntroduction />} */}
      <Footer />
    </div>
  );
};

export default Calculator;